import React, { useState } from 'react';
import SubframeCore from "@subframe/core";
// import { Progress } from "@/subframe/components/Progress";
import { IconButton } from "@/subframe/components/IconButton";
import moment from "moment";
import MessageComponent from "../MessageComponent";
// import { useSelector } from 'react-redux';
// import { useMediaDownload } from "../media/MediaDownloadHook";
// import { RootState } from '../../redux/store';
import { DialogLayout } from "@/subframe/layouts/DialogLayout";

interface RecipientMessage {
  message_id?: string;
  id?: string;
  recipient?: string;
  created_at: string;
  media_url?: string; // Added media_url property
  type?: string;
  recipient_payload?: {
    entry: [{
      changes: [{
        value: {
          messages: [{
            type: "text" | "video" | "document" | "image" | "audio" | "button" | "location";
            text?: {
              body: string;
            };
            referral?: {
              body?: string;
              headline?: string;
              image_url?: string;
              source_id?: string;
              media_type?: string;
              source_url?: string;
              source_type?: string;
            };
            video?: {
              caption?: string;
              id?: number;
            };
            document?: {
              caption?: string;
              id?: number;
              filename?: string;
              mime_type?: string;
            };
            image?: {
              caption?: string;
              id?: number;
            };
            audio?: {
              voice: boolean;
              id?: number;
              mime_type?: string;
              duration?: number;
            };
          }]
        }
      }]
    }]
  }
}

interface RecipientProps {
  message: RecipientMessage;
  scrollRef: React.RefObject<HTMLDivElement>;
}

const Recipient: React.FC<RecipientProps> = ({ message, scrollRef }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showReferralPreview, setShowReferralPreview] = useState(false);
  const messageData = message.recipient_payload?.entry?.[0]?.changes?.[0]?.value?.messages?.[0];
  const referral = messageData?.referral;
  // const system_user_id = useSelector((state: RootState) => state.auth.system_user_id);
  // const { downloadMedia } = useMediaDownload();


  // Helper function to get media URL
  const getMediaUrl = () => {
    return message.media_url;
  };

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPreview(true);
  };

  const getLocationEmbedUrl = (url: string) => {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}z=15&output=embed`;
  };

  const renderReferralSection = () => {
    if (!referral) return null;
    const imageUrl = referral.image_url;
    const sourceUrl = referral.source_url;

    return (
      <div className="flex w-full flex-col items-start gap-2 px-1 py-1">
        {imageUrl ? (
          <div className="relative flex w-full flex-col items-center justify-center gap-2 rounded-t-md bg-neutral-200 px-1 py-1">
            <img
                           className="h-36 w-full flex-none rounded-t-sm border-b-2 border-solid border-neutral-border object-cover"
              src={imageUrl}
              alt={referral.headline || "Ad"}
              loading="lazy"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowReferralPreview(true);
              }}
            />
          </div>
        ) : null}

        {sourceUrl ? (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-md bg-brand-600 px-3 py-2 text-body-bold font-body-bold text-white hover:bg-brand-700 transition-colors"
          >
            See Ad
          </a>
        ) : null}

        <div className="flex w-full flex-col gap-1 text-caption font-caption text-subtext-color">
          {referral.headline ? <div><span className="text-default-font">Headline:</span> {referral.headline}</div> : null}
          {referral.body ? <div><span className="text-default-font">Body:</span> {referral.body}</div> : null}
          {referral.source_id ? <div><span className="text-default-font">Source ID:</span> {referral.source_id}</div> : null}
          {referral.media_type ? <div><span className="text-default-font">Media type:</span> {referral.media_type}</div> : null}
          {referral.source_type ? <div><span className="text-default-font">Source type:</span> {referral.source_type}</div> : null}
        </div>
      </div>
    );
  };

  // Helper function to render preview content for dialog
  const renderPreview = () => {
    const mediaUrl = getMediaUrl();
    if (!mediaUrl) {
      return (
        <div className="flex items-center justify-center h-48 text-gray-500">
          <div className="text-center">
            <SubframeCore.Icon
              className="text-4xl text-gray-400 mb-2"
              name="FeatherImage"
            />
            <p>Media not available</p>
            <p className="text-sm text-gray-400">No media URL provided</p>
          </div>
        </div>
      );
    }

    if (messageData?.type === 'image') {
      return (
        <img
          className="max-h-96 w-auto flex-none rounded-md shadow-lg"
          src={mediaUrl}
          alt="Preview"
        />
      );
    }

    if (messageData?.type === 'video') {
      return (
        <video
          className="max-h-96 w-auto flex-none rounded-md shadow-lg"
          controls
        >
          <source src={mediaUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    if (messageData?.type === 'document') {
      return (
        <div className="w-full h-96">
          <embed
            src={mediaUrl}
            type={messageData?.document?.mime_type}
            width="100%"
            height="100%"
            className="rounded-md shadow-lg"
          />
        </div>
      );
    }

    if (messageData?.type === 'audio') {
      return (
        <audio
          className="w-full"
          controls
        >
          <source src={mediaUrl} type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      );
    }
  };

  if (message.type === 'location') {
    if (!message.recipient?.trim()) return null;

    return (
      <div ref={scrollRef} className="flex min-w-0 w-full max-w-full items-start justify-start gap-2 mt-2">
        <div className="flex min-w-0 max-w-[60%] flex-col items-start gap-2 overflow-hidden rounded-lg bg-default-background shadow-sm">
          <iframe
            width="400"
            height="300"
            className="max-w-full border-0"
            src={getLocationEmbedUrl(message.recipient.trim())}
            allowFullScreen
            title="Location"
          />
          <div className="flex w-full items-center gap-2 px-4 pb-3">
            <SubframeCore.Icon
              className="text-body font-body text-subtext-color"
              name="FeatherMapPin"
            />
            <span className="grow shrink-0 basis-0 text-body font-body text-default-font">
              Location
            </span>
            <span className="text-caption font-caption text-subtext-color">
              {moment(message.created_at).local().format("hh:mm A")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Handle basic text message or when no specific message type is provided
  if (!messageData && message.recipient) {
    return (
      <div ref={scrollRef} className="flex min-w-0 w-full max-w-full items-start justify-start gap-2 mt-2">
        <div className="flex min-w-0 max-w-[60%] flex-col items-start gap-2 overflow-hidden rounded-lg bg-default-background px-4 py-3 shadow-sm">
          <MessageComponent message={message.recipient} />
          <div className="flex items-end gap-1">
            <span className="text-caption font-caption text-subtext-color">
              {moment(message.created_at).local().format("hh:mm A")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  switch (messageData?.type) {
    case 'video':
      return (
        <>
          <div ref={scrollRef} className="flex min-w-0 w-full max-w-full flex-col items-start justify-start gap-2 mt-4">
            <div className="flex min-w-0 w-full max-w-[45%] flex-col items-end justify-center overflow-hidden rounded-lg border border-solid border-neutral-border bg-[#ffffffff] shadow-sm">
              <div className="flex min-w-0 w-full flex-col items-start gap-2 px-1 py-1">
                <div className="relative flex w-full flex-col items-center justify-center gap-2 rounded-t-md bg-neutral-200 px-1 py-1">
                  <img
                    className="h-36 w-full flex-none rounded-t-sm border-b-2 border-solid border-neutral-border object-cover blur-md"
                    src={getMediaUrl()}
                    alt="video preview"
                  />
                  <button
                    onClick={handlePreviewClick}
                    title="Play video"
                    aria-label="Play video"
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 backdrop-blur-md transition-transform duration-300 hover:scale-110 shadow-lg"
                  >
                    <SubframeCore.Icon
                      className="text-heading-1 font-heading-1 text-neutral-600 p-50"
                      name="FeatherArrowDownCircle"
                    />
                  </button>
                </div>
              </div>
              <div className="flex min-w-0 w-full grow shrink-0 basis-0 flex-col items-start gap-1 px-4 pt-2 pb-1">
                <MessageComponent message={message.recipient || ''} />
                <div className="flex min-w-0 w-full items-center justify-end gap-1">
                  <span className="text-caption font-caption text-subtext-color">
                    {moment(message.created_at).local().format("hh:mm A")}
                  </span>
                  <div className="flex min-w-0 grow shrink-0 basis-0 items-center justify-end gap-1">
                    <SubframeCore.Icon
                      className="text-monospace-body font-monospace-body text-subtext-color shrink-0"
                      name="FeatherVideo"
                    />
                    <span className="text-caption font-caption text-subtext-color">
                      Video message
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogLayout open={showPreview} onOpenChange={setShowPreview}>
            <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-6 py-6">
              <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Media Preview
                  </span>
                </div>
                <IconButton
                  icon="FeatherX"
                  onClick={() => setShowPreview(false)}
                />
              </div>

              <div className="relative w-full grow shrink-0 basis-0 flex-col items-center justify-center gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
                <div className="flex flex-col items-center">
                  {renderPreview()}
                </div>
              </div>
            </div>
          </DialogLayout>
        </>
      );

    case 'document':
      return (
        <>
          <div ref={scrollRef} className="flex min-w-0 w-full max-w-full flex-col items-start justify-center gap-4 mt-4">
            <div className="flex min-w-0 w-full max-w-[45%] flex-col items-end justify-center overflow-hidden rounded-lg border border-solid border-neutral-border bg-[#ffffffff] shadow-sm">
              <div className="flex min-w-0 w-full flex-col items-start px-1 py-1">
                <div className="relative flex w-full flex-col items-center justify-center gap-2 rounded-t-md bg-neutral-200 px-1 py-1">
                  <img
                    className="h-36 w-full flex-none rounded-t-sm border-b-2 border-solid border-neutral-border object-cover blur-md"
                    src={getMediaUrl()}
                    alt="document preview"
                  />
                  <button
                    onClick={handlePreviewClick}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 backdrop-blur-md transition-transform duration-300 hover:scale-110 shadow-lg"
                    aria-label="Preview document"
                    title="Preview document"
                  >
                    <SubframeCore.Icon
                      className="text-heading-1 font-heading-1 text-neutral-600 p-50"
                      name="FeatherArrowDownCircle"
                    />
                  </button>
                </div>
                <div className="flex min-w-0 w-full items-center gap-2 rounded-b-md bg-neutral-200 px-2 pt-1 pb-2.5">
                  <SubframeCore.Icon
                    className="text-heading-1 font-heading-1 shrink-0 text-default-font"
                    name="FeatherFile"
                  />
                  <div className="flex min-w-0 flex-1 flex-col items-start">
                    <span className="w-full break-words text-body-bold font-body-bold text-default-font">
                      {messageData.document?.filename || "Document"}
                    </span>
                    <span className="w-full break-all text-caption font-caption text-subtext-color">
                      {messageData.document?.mime_type?.split('/')[1]?.toUpperCase() ?? ""}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex min-w-0 w-full grow shrink-0 basis-0 flex-col items-start gap-1 px-4 pt-2 pb-1">
                <MessageComponent message={message.recipient || ''} />
                <span className="text-caption font-caption text-subtext-color">
                  {moment(message.created_at).local().format("hh:mm A")}
                </span>
              </div>
            </div>
          </div>

          <DialogLayout open={showPreview} onOpenChange={setShowPreview}>
            <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-6 py-6">
              <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Media Preview
                  </span>
                </div>
                <IconButton
                  icon="FeatherX"
                  onClick={() => setShowPreview(false)}
                />
              </div>

              <div className="relative w-full grow shrink-0 basis-0 flex-col items-center justify-center gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
                <div className="flex flex-col items-center">
                  {renderPreview()}
                </div>
              </div>
            </div>
          </DialogLayout>
        </>
      );

    case 'image':
      return (
        <>
          <div ref={scrollRef} className="flex min-w-0 w-full max-w-full flex-col items-start justify-center gap-4 mt-4">
            <div className="flex min-w-0 w-full max-w-[45%] flex-col items-end justify-center overflow-hidden rounded-lg border border-solid border-neutral-border bg-[#ffffffff] shadow-sm">
              <div className="flex min-w-0 w-full flex-col items-start gap-2 px-1 py-1">
                <div className="relative flex w-full flex-col items-center justify-center gap-2 rounded-t-md bg-neutral-200 px-1 py-1">
                  <img
                    className="h-36 w-full flex-none rounded-t-sm border-b-2 border-solid border-neutral-border object-cover blur-md"
                    src={getMediaUrl()}
                    alt="image preview"
                  />
                  <button
                    onClick={handlePreviewClick}
                    title="Open image preview"
                    aria-label="Open image preview"
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 backdrop-blur-md transition-transform duration-300 hover:scale-110 shadow-lg"
                  >
                    <SubframeCore.Icon
                      className="text-heading-1 font-heading-1 text-neutral-600 p-50"
                      name="FeatherArrowDownCircle"
                    />
                  </button>
                </div>
              </div>
              <div className="flex min-w-0 w-full grow shrink-0 basis-0 flex-col items-start gap-1 px-4 pt-2 pb-1">
                <MessageComponent message={message.recipient || ''} />
                <div className="flex min-w-0 w-full items-center justify-end gap-1">
                  <span className="text-caption font-caption text-subtext-color">
                    {moment(message.created_at).local().format("hh:mm A")}
                  </span>
                  <div className="flex min-w-0 grow shrink-0 basis-0 items-center justify-end gap-1">
                    <SubframeCore.Icon
                      className="text-monospace-body font-monospace-body text-subtext-color shrink-0"
                      name="FeatherImage"
                    />
                    <span className="text-caption font-caption text-subtext-color">
                      Image message
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogLayout open={showPreview} onOpenChange={setShowPreview}>
            <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-6 py-6">
              <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Media Preview
                  </span>
                </div>
                <IconButton
                  icon="FeatherX"
                  onClick={() => setShowPreview(false)}
                />
              </div>

              <div className="relative w-full grow shrink-0 basis-0 flex-col items-center justify-center gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
                <div className="flex flex-col items-center">
                  {renderPreview()}
                </div>
              </div>
            </div>
          </DialogLayout>
        </>
      );

    case 'audio':
      return (
        <div ref={scrollRef} className="flex min-w-0 w-full max-w-full items-start justify-start gap-2 mt-2">
          <div className="flex min-w-0 w-full max-w-[45%] flex-col items-start gap-2 overflow-hidden rounded-md border border-solid border-neutral-border bg-white px-4 py-3 shadow-sm">
            <audio
              className="w-full"
              controls
              src={getMediaUrl()}
              preload="metadata"
            >
              Your browser does not support the audio element.
            </audio>
            <MessageComponent message={message.recipient || ''} />
            <div className="flex w-full items-center gap-2">
              <SubframeCore.Icon
                className="text-body font-body text-subtext-color"
                name="FeatherMic"
              />
              <span className="grow shrink-0 basis-0 text-body font-body text-default-font">
                Voice message
              </span>
              <span className="text-caption font-caption text-subtext-color">
                {moment(message.created_at).local().format("hh:mm A")}
              </span>
            </div>
          </div>
        </div>
      );

    default:
      return (
        <>
          <div ref={scrollRef} className="flex min-w-0 w-full max-w-full items-start justify-start gap-2 mt-2">
            <div className="flex min-w-0 max-w-[60%] flex-col items-start gap-2 overflow-hidden rounded-lg bg-default-background px-4 py-3 shadow-sm">
              {renderReferralSection()}
              <MessageComponent message={message.recipient || ''} />
              <div className="flex items-end gap-1">
                <span className="text-caption font-caption text-subtext-color">
                  {moment(message.created_at).local().format("hh:mm A")}
                </span>
              </div>
            </div>
          </div>

          <DialogLayout open={showReferralPreview} onOpenChange={setShowReferralPreview}>
            <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-6 py-6">
              <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Ad Preview
                  </span>
                </div>
                <IconButton
                  icon="FeatherX"
                  onClick={() => setShowReferralPreview(false)}
                />
              </div>

              <div className="relative w-full grow shrink-0 basis-0 flex-col items-center justify-center gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
                <div className="flex flex-col items-center">
                  {referral?.image_url ? (
                    <img
                      className="max-h-[70vh] w-auto max-w-full rounded-md shadow-lg object-contain bg-neutral-50"
                      src={referral.image_url}
                      alt={referral.headline || "Ad"}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-48 text-gray-500">
                      <div className="text-center">
                        <SubframeCore.Icon
                          className="text-4xl text-gray-400 mb-2"
                          name="FeatherImage"
                        />
                        <p>Image not available</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogLayout>
        </>
      );
  }
};

export default Recipient;